<script setup>
import { CheckIcon } from "lucide-vue-next";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    as: {
        type: [Object, String],
        default: "button",
    },
    type: {
        type: String,
        default: "submit",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    processing: {
        type: Boolean,
        default: false,
    },
    success: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: [Object, Function],
        required: false,
    },
    z: {
        type: Number,
        default: 0,
    },
    class: String,
});

const { t: __ } = useI18n();

const _disabled = computed(() => {
    if (props.disabled || props.processing) {
        return true;
    }

    return false;
});

const buttonClasses = computed(() => {
    let classes = [
        _disabled.value ? "pointer-events-none" : "",
        "shadow-sm relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-md gap-2.5",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",

        // light
        "text-white bg-gray-800 hover:bg-gray-900 disabled:bg-gray-500 focus-visible:ring-gray-900",

        // dark
        "dark:text-gray-900 dark:bg-white dark:hover:bg-gray-200 dark:disabled:bg-white dark:disabled:opacity-50 dark:focus-visible:ring-white",
    ];

    switch (props.z) {
        case 3:
            classes.push("focus-visible:ring-offset-gray-200 dark:focus-visible:ring-offset-gray-700");
            break;
        case 2:
            classes.push("focus-visible:ring-offset-gray-100 dark:focus-visible:ring-offset-gray-800");
            break;
        case 1:
            classes.push("focus-visible:ring-offset-gray-50 dark:focus-visible:ring-offset-gray-900");
            break;
        default:
            classes.push("focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-950");
            break;
    }

    return twMerge(classes, props.class);
});
</script>

<template>
    <component :is="as" :type="type" :disabled="_disabled" :class="buttonClasses">
        <CheckIcon v-if="success" class="flex-shrink-0 w-4 h-4" />
        <svg v-else-if="processing" class="flex-shrink-0 w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <slot v-else name="icon">
            <component v-if="icon" :is="icon" class="flex-shrink-0 w-4 h-4" />
        </slot>
        <slot />
    </component>
</template>
